import { useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/feature/AppLayout";
import SectionHeader from "@/components/feature/SectionHeader";
import NovelCard from "@/components/feature/NovelCard";
import ArtCard from "@/components/feature/ArtCard";
import StoryCard from "@/components/feature/StoryCard";
import useSeo from "@/hooks/useSeo";
import { novels, artWorks, shortStories, faqs, testimonials, siteName, siteSlogan } from "@/mocks/narutoContent";

const categories = [
  {
    name: "同人小说",
    desc: "长篇连载与中短篇，围绕角色、羁绊与羁縻写尽木叶江湖",
    path: "/xiaoshuo",
    icon: "ri-book-open-line",
    cover: "https://readdy.ai/api/search-image?query=open%20storybook%20with%20anime%20ninja%20scenes%20coming%20out%20of%20pages%2C%20warm%20golden%20light%2C%20ink%20wash%20japanese%20style%2C%20cozy%20reading%20atmosphere%2C%20high%20detail&width=800&height=560&seq=naruto-cat-novel&orientation=landscape",
  },
  {
    name: "同人图 · 插画",
    desc: "海量同人插画，角色立绘、战斗名场面与温情日常一网打尽",
    path: "/tongrentu",
    icon: "ri-palette-line",
    cover: "https://readdy.ai/api/search-image?query=art%20studio%20with%20colorful%20anime%20ninja%20character%20paintings%20on%20canvas%20and%20palette%20brushes%2C%20warm%20light%2C%20ink%20wash%20japanese%20style%2C%20creative%20mood%2C%20high%20detail&width=800&height=560&seq=naruto-cat-art&orientation=landscape",
  },
  {
    name: "高清壁纸",
    desc: "适配电脑与手机的高清同人壁纸，四季风景与热血名场面",
    path: "/tongrentu",
    icon: "ri-image-line",
    cover: "https://readdy.ai/api/search-image?query=ultrawide%20anime%20wallpaper%20preview%20of%20a%20ninja%20village%20at%20sunset%20displayed%20on%20a%20desktop%20monitor%2C%20warm%20orange%20tones%2C%20ink%20wash%20japanese%20style%2C%20clean%20minimal%20setup%2C%20high%20detail&width=800&height=560&seq=naruto-cat-wall&orientation=landscape",
  },
  {
    name: "短篇速读",
    desc: "每日精选短篇，五六分钟读完一篇走心同人，通勤路上的小确幸",
    path: "/duanpian",
    icon: "ri-flashlight-line",
    cover: "https://readdy.ai/api/search-image?query=cozy%20reading%20corner%20with%20a%20cup%20of%20tea%20and%20open%20notebook%20beside%20a%20window%20with%20warm%20morning%20light%20and%20falling%20leaves%2C%20ink%20wash%20japanese%20style%2C%20calm%20healing%20mood%2C%20high%20detail&width=800&height=560&seq=naruto-cat-short&orientation=landscape",
  },
];

export default function Home() {
  const hotNovels = novels.filter((n) => n.hot);
  const hotArts = artWorks.filter((a) => a.hot);
  const [activeStory, setActiveStory] = useState(0);
  const [activeFaq, setActiveFaq] = useState(0);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "影页社 · 热门同人内容",
    itemListElement: hotNovels.map((n, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: n.title,
      url: `https://wn-baidu.cn/duanpian/s1`,
    })),
  };

  useSeo({
    title: "影页社 - 火影忍者同人小说·同人图·壁纸·短篇精选",
    description:
      "影页社专注火影忍者同人图文与小说内容，每日精选火影忍者同人小说、同人文及同人图，提供高清火影忍者同人壁纸、插画下载与短篇速读推荐，满足同人爱好者一站式看图读文需求。",
    keywords: "火影忍者同人,火影忍者同人小说,火影忍者同人图,火影忍者同人文,火影忍者同人壁纸,火影忍者同人短篇",
    canonical: "https://wn-baidu.cn/",
    jsonLd: [faqSchema, itemListSchema],
  });

  return (
    <AppLayout overlay>
      {/* ===== 首屏 ===== */}
      <section className="relative flex min-h-[600px] w-full items-center justify-center overflow-hidden md:h-[640px]">
        <img
          src="https://readdy.ai/api/search-image?query=cinematic%20ink%20wash%20painting%20of%20a%20ninja%20village%20silhouette%20with%20towering%20mountain%20faces%20under%20a%20dramatic%20warm%20orange%20sunset%20sky%2C%20swirling%20wind%20carrying%20autumn%20leaves%20and%20a%20giant%20spiral%20cloud%20formation%2C%20japanese%20scroll%20art%20style%2C%20epic%20scale%2C%20rich%20amber%20and%20deep%20ink%20black%20contrast%2C%20high%20detail&width=1600&height=900&seq=hero-naruto-main&orientation=landscape"
          alt="火影忍者同人 · 水墨忍者村与橙色落日"
          title="火影忍者同人壁纸 · 忍者村水墨"
          className="absolute inset-0 h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/60 via-foreground-950/30 to-background-50"></div>

        <div className="relative z-10 mx-auto flex w-full flex-col items-center px-4 text-center pt-24 pb-20 md:pt-28">
          <span className="mb-5 inline-flex items-center gap-2 rounded-full border border-background-100/40 bg-background-50/10 px-4 py-1.5 font-label text-xs tracking-widest text-background-50 backdrop-blur-sm">
            <i className="ri-sparkling-2-line text-primary-300"></i>
            火影忍者同人图文精选 · 每日更新
          </span>
          <h1 className="font-display text-4xl leading-tight text-background-50 md:text-6xl md:leading-tight">
            {siteName}
          </h1>
          <p className="mt-3 font-display text-xl text-primary-300 md:text-2xl">{siteSlogan}</p>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-background-100/90 md:text-base">
            同人小说、同人图、高清壁纸与短篇速读，一站式看图读文。与千万同好一起，重温那个关于忍道、羁绊与回家的故事。
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/xiaoshuo"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-primary-500 px-7 py-3 font-label text-sm whitespace-nowrap text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-book-open-line"></i> 开始读文
            </Link>
            <Link
              to="/tongrentu"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-background-100/40 bg-background-50/10 px-7 py-3 font-label text-sm whitespace-nowrap text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/20"
            >
              <i className="ri-image-line"></i> 浏览同人图
            </Link>
          </div>
        </div>
      </section>

      {/* ===== 主题分类 ===== */}
      <section className="mx-auto w-full max-w-7xl px-4 md:px-6 py-14 md:py-20">
        <SectionHeader
          eyebrow="影页社栏目"
          title="四大同人内容板块"
          subtitle="无论你是想一口气追完长篇同人小说，还是收藏高清壁纸，或是在通勤路上读一篇走心短篇，这里都有属于你的木叶一隅。"
        />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {categories.map((cat) => (
            <Link
              key={cat.name}
              to={cat.path}
              className="group relative overflow-hidden rounded-lg border border-background-200 bg-background-100"
            >
              <div className="relative h-36 overflow-hidden">
                <img
                  src={cat.cover}
                  alt={`火影忍者同人 ${cat.name}`}
                  title={`火影忍者同人${cat.name}`}
                  loading="lazy"
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 to-transparent"></div>
                <span className="absolute bottom-3 left-4 grid h-9 w-9 place-items-center rounded-full bg-background-50/90 text-primary-700">
                  <i className={`${cat.icon} text-lg`}></i>
                </span>
              </div>
              <div className="p-4 md:p-5">
                <h3 className="font-display text-xl text-foreground-950 group-hover:text-primary-700 transition-colors">{cat.name}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{cat.desc}</p>
                <span className="mt-4 inline-flex items-center gap-1 font-label text-xs text-primary-600 group-hover:gap-2 transition-all">
                  前往栏目 <i className="ri-arrow-right-line"></i>
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ===== 热门同人小说 ===== */}
      <section className="bg-background-100/70">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-6 py-14 md:py-20">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-8 md:mb-10">
            <SectionHeader
              align="left"
              eyebrow="热门连载"
              title="同人小说热榜"
              subtitle="同好们最爱追的火影忍者同人小说，情节、人物与羁绊都足够动人。"
            />
            <Link to="/xiaoshuo" className="inline-flex items-center gap-1 self-start rounded-full border border-primary-300 px-4 py-2 font-label text-sm whitespace-nowrap text-primary-700 transition-colors hover:bg-primary-500 hover:text-background-50 sm:self-auto">
              查看全部小说 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {hotNovels.map((n) => (
              <NovelCard key={n.id} novel={n} />
            ))}
          </div>
        </div>
      </section>

      {/* ===== 同人图 · 壁纸精选 ===== */}
      <section className="mx-auto w-full max-w-7xl px-4 md:px-6 py-14 md:py-20">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-8 md:mb-10">
          <SectionHeader
            align="left"
            eyebrow="视觉精选"
            title="同人图 · 高清壁纸"
            subtitle="从热血战斗到温情日常，精选火影忍者同人插画与高清壁纸，满足看图与收藏的双重需求。"
          />
          <Link to="/tongrentu" className="inline-flex items-center gap-1 self-start rounded-full border border-primary-300 px-4 py-2 font-label text-sm whitespace-nowrap text-primary-700 transition-colors hover:bg-primary-500 hover:text-background-50 sm:self-auto">
            进入画廊 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {hotArts.map((a) => (
            <ArtCard key={a.id} work={a} />
          ))}
        </div>
      </section>

      {/* ===== 短篇速读 ===== */}
      <section className="bg-background-100/70">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-6 py-14 md:py-20">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-8 md:mb-10">
            <SectionHeader
              align="left"
              eyebrow="每日推送"
              title="短篇速读推荐"
              subtitle="五六分钟读完一篇走心同人，治愈、热血与温情都藏在字里行间。"
            />
            <Link to="/duanpian" className="inline-flex items-center gap-1 self-start rounded-full border border-primary-300 px-4 py-2 font-label text-sm whitespace-nowrap text-primary-700 transition-colors hover:bg-primary-500 hover:text-background-50 sm:self-auto">
              更多短篇 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            {shortStories.map((s, i) => (
              <StoryCard key={s.id} story={s} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ===== 口碑 ===== */}
      <section className="mx-auto w-full max-w-7xl px-4 md:px-6 py-14 md:py-20">
        <SectionHeader
          eyebrow="同好之声"
          title="读者与作者怎么说"
          subtitle="来自追更读者、壁纸收藏者与投稿画手的真实反馈，也是我们持续更新的动力。"
        />
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
          {testimonials.map((t) => (
            <figure key={t.name} className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <div className="flex items-center gap-1 text-primary-500">
                {[1, 2, 3, 4, 5].map((s) => (
                  <i key={s} className="ri-star-fill text-sm"></i>
                ))}
              </div>
              <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-foreground-700">“{t.text}”</blockquote>
              <figcaption className="mt-5 flex items-center gap-3 border-t border-background-200 pt-4">
                <span className="grid h-10 w-10 place-items-center rounded-full bg-primary-100 font-display text-lg text-primary-700">
                  {t.name[0]}
                </span>
                <div>
                  <p className="font-label text-sm text-foreground-900">{t.name}</p>
                  <p className="font-label text-xs text-foreground-500">{t.role}</p>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* ===== 短篇速读 · 精选展示 ===== */}
      <section className="bg-background-950 text-background-100">
        <div className="mx-auto grid w-full max-w-7xl gap-8 px-4 md:px-6 py-14 md:py-20 lg:grid-cols-2 items-center">
          <div>
            <span className="mb-3 inline-flex items-center gap-2 font-label text-xs tracking-widest text-primary-400">
              <i className="ri-flashlight-line"></i> 本周精选短篇
            </span>
            <h2 className="font-display text-3xl md:text-4xl text-background-50">今天，想读一篇怎样的同人？</h2>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-300">
              每日从同好投稿中精选一篇走心短篇，五六分钟读完。这里有治愈、有热血，也有关于羁绊与归途的温柔想象。
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {shortStories.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setActiveStory(i)}
                  className={`rounded-full px-4 py-1.5 font-label text-xs whitespace-nowrap transition-colors ${
                    activeStory === i ? "bg-primary-500 text-background-50" : "bg-secondary-900 text-background-300 hover:bg-secondary-800"
                  }`}
                >
                  {s.title}
                </button>
              ))}
            </div>
            <Link
              to={`/duanpian/${shortStories[activeStory].id}`}
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-3 font-label text-sm whitespace-nowrap text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-book-2-line"></i> 阅读《{shortStories[activeStory].title}》
            </Link>
          </div>
          <div className="overflow-hidden rounded-lg border border-secondary-800">
            <img
              src={shortStories[activeStory].cover}
              alt={`${shortStories[activeStory].title} 火影忍者同人短篇`}
              title={`${shortStories[activeStory].title} · 火影忍者同人短篇速读`}
              className="h-80 w-full object-cover object-top md:h-96"
            />
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="mx-auto w-full max-w-4xl px-4 md:px-6 py-14 md:py-20">
        <SectionHeader
          eyebrow="常见问题"
          title="关于影页社，你可能想知道"
          subtitle="整理了同好们最常问的几个问题，希望能帮你更快上手影页社。"
        />
        <div className="space-y-3">
          {faqs.map((f, i) => {
            const open = activeFaq === i;
            return (
              <div key={f.q} className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
                <button
                  type="button"
                  onClick={() => setActiveFaq(open ? -1 : i)}
                  aria-expanded={open}
                  className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                >
                  <span className="font-label text-sm font-medium text-foreground-900 md:text-base">{f.q}</span>
                  <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-full transition-colors ${open ? "bg-primary-500 text-background-50" : "bg-secondary-100 text-secondary-800"}`}>
                    <i className={`${open ? "ri-subtract-line" : "ri-add-line"}`}></i>
                  </span>
                </button>
                {open && (
                  <div className="px-5 pb-5 text-sm leading-relaxed text-foreground-600">
                    <p>{f.a}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </AppLayout>
  );
}